import { FlagFilled } from "@ant-design/icons";
import { Button, Col, Flex, Grid, Row, Steps, Typography } from "antd";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import BEVZsvg from "../../assets/v2/bevzTextLogo.svg";
import Footer from "../../components/v2/Footer/Footer";
import {
  ACTIONTYPES,
  useStore,
  useStoreDispatch,
} from "../../context/CreateStoreContext";
import AccountInformation from "./AccountInformation/AccountInformation";
import BusinessInfo from "./BusinessInfo/BusinessInfo";
import ContactInfo from "./ContactInfo/ContactInfo";
import style from "./Create.module.css";
import Review from "./Review/Review";
import StoreHours from "./StoreHours/StoreHours";
import StoreInformation from "./StoreInformation/StoreInformation";
import SubsPlan from "./SubsPlan/SubsPlan";
import accountTypes from "../../constants/accountTypes";

const { Paragraph } = Typography;
const { useBreakpoint } = Grid;

// Issue related to Steps affecting vertical and horizontal direction
// https://github.com/ant-design/ant-design/issues/21294

export default function Create() {
  const screen = useBreakpoint();
  const {
    accountType,
    steps: { currentIndex = 0, lastStepIndex: _lastStepindex = 0 },
    stepItems = [],
  } = useStore();
  const dispatch = useStoreDispatch();
  const [current, setCurrent] = useState(currentIndex);
  const [lastStepIndex, setLastStepIndex] = useState(_lastStepindex);
  const [steps, setSteps] = useState(stepItems);
  const [stepsDirection, setStepsDirection] = useState("large");
  const formContainerRef = useRef(null);

  useEffect(() => {
    const { xs, sm, md, lg } = screen;
    setStepsDirection(() =>
      xs === true ||
      (xs === false && sm === true && md === false && lg === false)
        ? "small"
        : "large",
    );
  }, [screen]);

  useEffect(() => {
    const updatedSteps = steps.map((step, index) => {
      // Current step always gets process status, overriding any other status
      if (index === current) {
        return { ...step, status: "process", disabled: false };
      }

      // For non-current steps, preserve disabled or finish status
      if (step.disabled) {
        return { ...step, status: "wait" };
      }
      if (step.status === "finish") {
        return step;
      }

      // Previous steps get finish status
      if (index < current || index < lastStepIndex) {
        return { ...step, status: "finish", disabled: false };
      }

      // Future steps get wait status
      return { ...step, status: "wait" };
    });

    setLastStepIndex((prev) => (prev < current ? current : prev));
    setSteps(updatedSteps);
    localStorage.setItem("integratorFormCompleted", 0);

    dispatch({
      type: ACTIONTYPES.STEPS,
      data: {
        currentIndex: current,
        lastStepIndex,
        stepItems: updatedSteps.map(({ icon, ...rest }) => rest),
      },
    });
  }, [current]);

  const onChange = (value) => {
    // Prevent selecting disabled steps
    if (steps[value].disabled) {
      return;
    }
    setCurrent(value);
  };

  const handleNext = () => {
    setCurrent((current) => current + 1);
  };

  const handleBack = () => {
    setCurrent((current) => current - 1);
  };

  useEffect(() => {
    if (formContainerRef && formContainerRef.current) {
      formContainerRef.current.scrollTop = 0; // Reset scroll position of the container
    }
  }, [formContainerRef, current]);

  return (
    <>
      <Row>
        <Col xs={24} md={9} className={style.Left}>
          <Row justify={{ xs: "start", lg: "end" }}>
            <Col
              xs={{ span: 22, offset: 1 }}
              lg={{ span: 15, pull: 4 }}
              xl={{ span: 15, pull: 2 }}
            >
              <Flex vertical>
                <img
                  src={BEVZsvg}
                  alt="Bevz."
                  width="65px"
                  height="31.83px"
                  className={style.BevzLogoText}
                />
                <Paragraph className={style.Left_subheading}>
                  {accountType === accountTypes.store
                    ? "Add new store to your account."
                    : "Create your first store account."}
                </Paragraph>

                <Steps
                  direction={
                    stepsDirection === "small" ? "horizontal" : "vertical"
                  }
                  responsive={stepsDirection === "small" ? false : true}
                  size={stepsDirection === "small" ? "small" : "default"}
                  current={current}
                  onChange={onChange}
                  items={steps.map((step, index) => {
                    const { title, description, ..._step } = step;
                    return {
                      ..._step,
                      title: stepsDirection === "small" ? null : title,
                      description:
                        stepsDirection === "small" ? null : description,
                      icon: index === 6 ? <FlagFilled /> : null,
                    };
                  })}
                />
              </Flex>
            </Col>
          </Row>
        </Col>
        <Col xs={24} md={15} className={style.Right} ref={formContainerRef}>
          {/* <Button onClick={handleNext}>Proceed</Button>
          <Button onClick={handleBack}>Back</Button>
          <Button
            onClick={() => {
              localStorage.setItem("integratorFormCompleted", 1);
            }}
          >
            Finish
          </Button> */}
          {current === 0 ? <AccountInformation proceed={handleNext} /> : null}
          {current === 1 ? (
            <StoreInformation proceed={handleNext} back={handleBack} />
          ) : null}
          {current === 2 ? (
            <ContactInfo proceed={handleNext} back={handleBack} />
          ) : null}
          {current === 3 ? (
            <BusinessInfo proceed={handleNext} back={handleBack} />
          ) : null}
          {current === 4 ? (
            <StoreHours proceed={handleNext} back={handleBack} />
          ) : null}
          {current === 5 ? (
            <SubsPlan proceed={handleNext} back={handleBack} />
          ) : null}
          {current === 6 ? (
            <Review back={handleBack} handleGotoStep={onChange} />
          ) : null}
        </Col>
      </Row>
      <Footer />
    </>
  );
}
